import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import axios from "axios";
import {
  Mail, Lock, Eye, EyeOff, Phone, ArrowLeft, RotateCcw, Compass,
  Download, Hash, CheckCircle, Hammer, Scale, Users, AlertCircle,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { Helmet } from "react-helmet-async";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { usePWAInstall } from "@/hooks/usePWAInstall";
import { landingPath } from "@/lib/landing";
import { getRecaptchaToken } from "@/lib/recaptcha";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Compass Logo ─────────────────────────────────────────────────────────────
const CompassLogo = ({ size = 80, className = "" }) => (
  <img
    src="/compass-logo.png"
    alt="Compass"
    width={size}
    height={size}
    style={{ width: size, height: size, objectFit: "contain" }}
    className={className}
  />
);

// ─── Password Strength ────────────────────────────────────────────────────────
const PasswordStrength = ({ password }) => {
  if (!password) return null;
  const checks = [{ ok: password.length >= 16, label: "16+ chars" }];
  const passed = checks.filter((c) => c.ok).length;
  const color = passed <= 0 ? "#ef4444" : "#22c55e";
  return (
    <div className="mt-2 space-y-1.5">
      <div className="flex gap-1">
        {checks.map((_, i) => (
          <div key={i} className="h-1 flex-1 rounded-full transition-colors duration-300"
            style={{ backgroundColor: i < passed ? color : "#e5e7eb" }} />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-0.5">
        {checks.map((c, i) => (
          <span key={i} className="text-xs flex items-center gap-1"
            style={{ color: c.ok ? "#22c55e" : "#9ca3af" }}>
            <span>{c.ok ? "✓" : "○"}</span> {c.label}
          </span>
        ))}
      </div>
    </div>
  );
};

// ─── Google Icon ──────────────────────────────────────────────────────────────
const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
    <path fill="#4285F4" d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 0 0 2.38-5.88c0-.57-.05-.66-.15-1.18z"/>
    <path fill="#34A853" d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2a4.8 4.8 0 0 1-7.18-2.54H1.83v2.07A8 8 0 0 0 8.98 17z"/>
    <path fill="#FBBC05" d="M4.5 10.52a4.8 4.8 0 0 1 0-3.04V5.41H1.83a8 8 0 0 0 0 7.18l2.67-2.07z"/>
    <path fill="#EA4335" d="M8.98 4.18c1.17 0 2.23.4 3.06 1.2l2.3-2.3A8 8 0 0 0 1.83 5.4L4.5 7.49a4.77 4.77 0 0 1 4.48-3.3z"/>
  </svg>
);

const GoogleLoginSection = ({ onClick, testId = "google-login-button" }) => (
  <div className="mt-4 space-y-3">
    <div className="relative">
      <div className="absolute inset-0 flex items-center">
        <span className="w-full border-t border-gray-200" />
      </div>
      <div className="relative flex justify-center text-xs uppercase">
        <span className="bg-white px-2 text-gray-400 tracking-wider">or</span>
      </div>
    </div>
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center justify-center gap-3 py-2 px-4 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors shadow-sm"
      data-testid={testId}
    >
      <GoogleIcon />
      Continue with Google
    </button>
  </div>
);

// ─── Page Shell ───────────────────────────────────────────────────────────────
const PageShell = ({ children }) => {
  const { install, reinstall, isInstalled } = usePWAInstall();

  // Session 154 — The install button must ALWAYS produce a visible
  // result; the previous behavior silently no-op'd when no
  // `beforeinstallprompt` event was available (already-installed,
  // Safari iOS, Firefox, repeat visits where Chrome stopped firing
  // the event). User feedback: "the 'Install Compass App' button
  // isn't doing anything when clicked. It should install a PWA, or
  // re-install if the PWA is already installed in order to update
  // the icon and other installation."
  //
  // Behavior matrix:
  //   • Native prompt available  → fire native install dialog.
  //     ─ accepted  → success toast.
  //     ─ dismissed → "no harm done" toast.
  //   • Already installed OR no prompt available
  //     → confirm-style toast offering to refresh the installation
  //       (unregister SW + clear caches + reload). This pulls the
  //       latest manifest/icons and gives Chrome a chance to re-fire
  //       its install heuristic.
  const handleInstallClick = async () => {
    const { outcome } = await install();
    if (outcome === "accepted") {
      toast.success("Compass added to your device");
      return;
    }
    if (outcome === "dismissed") {
      toast("Install cancelled — you can run this again anytime.");
      return;
    }
    // outcome === "unavailable" → either already installed OR the
    // browser can't surface a native install dialog. Offer the
    // refresh-install path so the user can update the icon/manifest.
    toast(
      isInstalled
        ? "Compass is already installed — refresh installation to update icon?"
        : "Native install isn't available on this browser. Refresh installation now?",
      {
        action: {
          label: "Refresh install",
          onClick: () => reinstall(),
        },
        duration: 8000,
      },
    );
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Branding header — logo, tagline, install button */}
          <div className="text-center mb-[1.2rem]">
            <img
              src="/compass-logo.png"
              alt="Compass"
              className="mx-auto mb-4 w-16 h-16 md:w-20 md:h-20"
              style={{ objectFit: "contain" }}
            />
            {/* "Compass Family Law Portal" h1 removed — name appears in the login card */}

            {/* Install button — right below the logo.
                Session 143 — ALWAYS visible (was previously gated on
                `canInstall`, which only resolves true on Chrome /
                Edge / Samsung BEFORE the PWA is installed). Users on
                Safari iOS, Firefox, or browsers that have already
                installed the PWA never saw the pill — so they had no
                way to discover the install flow OR re-install when a
                new version shipped. The InstallModal now adapts its
                CTA to the platform (native prompt → tap the button;
                no prompt available → platform-specific instructions). */}
            <button
              onClick={handleInstallClick}
              data-testid="pwa-install-button"
              aria-label="Install Compass as an app"
              className="mt-3 inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-medium transition-all hover:opacity-90"
              style={{ backgroundColor: "#001C60", color: "#fff" }}
            >
              <Download className="h-3.5 w-3.5" />
              Install Compass App
            </button>
          </div>

          {children}

          {/* Footer */}
          <p className="text-center text-gray-500 text-sm mt-6">
            Need help? Contact{" "}
            <a
              href="mailto:maryann@kinglawnc.com"
              className="hover:underline transition-colors"
              style={{ color: "#001C60" }}
            >
              maryann@kinglawnc.com
            </a>
          </p>
          <a
            href="https://videos.kinglawnc.com/gettingstarted"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 text-sm hover:opacity-75 transition-opacity mt-4"
            style={{ color: "#001C60" }}
            data-testid="watch-intro-link"
          >
            <Compass className="h-4 w-4" aria-hidden="true" />
            <span>Watch Intro to Compass</span>
          </a>
        </div>
      </div>
    </div>
  );
};

const NavyButton = ({ children, className = "", ...props }) => (
  <button
    className={`w-full py-2 px-4 rounded-md text-sm font-medium text-white shadow transition-all
      duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 ${className}`}
    style={{ backgroundColor: "#001C60" }}
    {...props}
  >
    {children}
  </button>
);

const ErrorMsg = ({ msg, testId }) =>
  msg ? (
    <div
      className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3 animate-in fade-in"
      data-testid={testId || "error-message"}
    >
      {msg}
    </div>
  ) : null;

const SuccessMsg = ({ msg, testId }) =>
  msg ? (
    <div
      className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-lg p-3 animate-in fade-in"
      data-testid={testId || "success-message"}
    >
      {msg}
    </div>
  ) : null;

// ─── Signup — Role Picker ────────────────────────────────────────────────────
const SIGNUP_ROLES = [
  {
    type: "DIY",
    label: "Self-Service",
    icon: Hammer,
    tagline: "I want to handle my family law case myself.",
    detail: "Access Compass wizards, document generation, and legal tools to navigate your case independently. No law firm required.",
    accent: "#ea580c",  // orange
    confirm: "You want to create an account as a self-service user?",
  },
  {
    type: "Client",
    label: "Law Firm Client",
    icon: Users,
    tagline: "I'm hiring a law firm for representation.",
    detail: "Your law firm will provide a firm code. You'll be linked to your attorney's portal for secure document sharing and case collaboration.",
    accent: "#059669",  // emerald
    confirm: "You are hiring a law firm and want to create a client account?",
  },
  {
    type: "Lawyer",
    label: "Law Firm Employee",
    icon: Scale,
    tagline: "I work at a law firm already on Compass.",
    detail: "Join your firm's portal to manage clients, run wizards, and collaborate with your team. Requires your firm's code.",
    accent: "#001C60",  // navy
    confirm: "You want to create an account as a law firm employee?",
  },
];

const SignupRolePicker = ({ onPick }) => (
  <div className="space-y-3" data-testid="signup-role-picker">
    <div className="text-center mb-2">
      <h3 className="text-base font-semibold text-gray-900">What type of account?</h3>
      <p className="text-xs text-gray-500 mt-1">Pick the option that best describes you.</p>
    </div>
    {SIGNUP_ROLES.map((r) => {
      const Icon = r.icon;
      return (
        <button
          key={r.type}
          type="button"
          onClick={() => onPick(r.type)}
          data-testid={`signup-role-${r.type.toLowerCase()}`}
          className="w-full flex items-start gap-3 p-4 border-2 border-gray-200 rounded-xl hover:shadow-md transition-all duration-200 text-left group bg-white"
          style={{ borderColor: "transparent" }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = r.accent; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e5e7eb"; }}
        >
          <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
               style={{ backgroundColor: `${r.accent}1A`, color: r.accent }}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-gray-900 text-sm">{r.label}</div>
            <div className="text-xs text-gray-600 mt-0.5">{r.tagline}</div>
            <div className="text-[11px] text-gray-500 mt-1 leading-relaxed">{r.detail}</div>
          </div>
        </button>
      );
    })}
  </div>
);

const SignupConfirm = ({ type, onBack, onConfirm }) => {
  const role = SIGNUP_ROLES.find((r) => r.type === type);
  if (!role) return null;
  const Icon = role.icon;
  return (
    <div className="space-y-4 py-2" data-testid={`signup-confirm-${type.toLowerCase()}`}>
      <div className="flex flex-col items-center text-center">
        <div className="w-14 h-14 rounded-full flex items-center justify-center mb-3"
             style={{ backgroundColor: `${role.accent}1A`, color: role.accent }}>
          <Icon className="w-7 h-7" />
        </div>
        <h3 className="text-base font-semibold text-gray-900">{role.confirm}</h3>
        <p className="text-xs text-gray-500 mt-2 max-w-xs">{role.detail}</p>
      </div>
      <div className="flex gap-2 pt-2">
        <button type="button" onClick={onBack}
          data-testid="signup-confirm-back"
          className="flex-1 py-2 px-4 rounded-md text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-center gap-1.5">
          <ArrowLeft className="w-4 h-4" /> Not quite
        </button>
        <button type="button" onClick={onConfirm}
          data-testid="signup-confirm-yes"
          className="flex-1 py-2 px-4 rounded-md text-sm font-medium text-white transition-colors"
          style={{ backgroundColor: role.accent }}>
          Yes, continue
        </button>
      </div>
    </div>
  );
};

// Session 359 — Client ⇄ DIY toggle at the top of the signup form.
const SIGNUP_TOGGLE = [
  { type: "Client", label: "Law Firm Client", sub: "Hire Firm" },
  { type: "DIY",    label: "Self-Service",  sub: "Access Tools" },
];

const SignupTypeToggle = ({ type, onChange }) => (
  <div className="grid grid-cols-2 gap-2 mb-3" role="radiogroup" aria-label="Account type"
       data-testid="signup-form-header">
    {SIGNUP_TOGGLE.map((opt) => {
      const role = SIGNUP_ROLES.find((r) => r.type === opt.type);
      const Icon = role.icon;
      const active = type === opt.type;
      return (
        <button key={opt.type} type="button" role="radio" aria-checked={active}
          onClick={() => onChange(opt.type)}
          data-testid={`signup-type-${opt.type.toLowerCase()}`}
          className={`flex items-center gap-2 p-3 rounded-lg border-2 text-left transition-colors duration-150 ${
            active ? "bg-white shadow-sm" : "bg-gray-50 border-gray-200 hover:bg-white"}`}
          style={active ? { borderColor: role.accent } : undefined}>
          <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
               style={{ backgroundColor: `${role.accent}1A`, color: role.accent }}>
            <Icon className="w-3.5 h-3.5" />
          </div>
          <div className="min-w-0">
            <div className="text-xs font-medium text-gray-900">{opt.label}</div>
            <div className="text-[10px] text-gray-500">{opt.sub}</div>
          </div>
        </button>
      );
    })}
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const LoginPage = () => {
  const navigate = useNavigate();

  // Session 116 — cross-tab login awareness.
  //
  // (1) On mount, if a `compass_token` already exists in localStorage
  //     (the user is logged in on another tab, OR just hit the bookmark
  //     while still authenticated), validate it against /api/auth/me.
  //     If valid → redirect to /dashboard. If invalid (401) → clear
  //     the stale token and show the form normally.
  // (2) Listen for `storage` events so a login completed in another
  //     tab follows-through here in real time. Once the token shows up,
  //     we redirect to /dashboard without forcing a refresh.
  //
  // No 2FA loop happens because the JWT is only ever issued AFTER the
  // 2FA challenge in the originating tab — every subsequent tab just
  // reads the already-issued token.
  const [bootstrapping, setBootstrapping] = useState(true);

  // Where to land after a successful login. Defaults to the role-based
  // landing path (see @/lib/landing), but a
  // `?next=<internal-path>` (e.g. from an emailed "Upload now" link) takes
  // precedence. Only same-origin paths are honored — no open redirects.
  const getNextPath = () => {
    const n = new URLSearchParams(window.location.search).get("next");
    if (n && n.startsWith("/") && !n.startsWith("//")) return n;
    return null;
  };

  useEffect(() => {
    let cancelled = false;

    const validateExisting = async () => {
      const token = localStorage.getItem("compass_token");
      if (!token) {
        if (!cancelled) setBootstrapping(false);
        return;
      }
      try {
        await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) navigate(getNextPath() || landingPath(), { replace: true });
      } catch {
        // 401 / network — clear and let the form render.
        ["compass_token", "compass_user_type", "compass_first_name",
         "compass_last_name", "compass_user_id", "compass_is_master"
        ].forEach((k) => localStorage.removeItem(k));
        if (!cancelled) setBootstrapping(false);
      }
    };

    validateExisting();

    // Cross-tab login: if any other tab writes `compass_token`,
    // follow it. (Storage events only fire in OTHER tabs, never
    // the one that mutated localStorage — exactly what we want.)
    const onStorage = (e) => {
      if (e.key === "compass_token" && e.newValue) {
        navigate(getNextPath() || landingPath(), { replace: true });
      }
    };
    window.addEventListener("storage", onStorage);
    return () => {
      cancelled = true;
      window.removeEventListener("storage", onStorage);
    };
  }, [navigate]);

  // Flow
  const [step, setStep]         = useState("credentials");
  const [activeTab, setActiveTab] = useState("login");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError]       = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [userId, setUserId]               = useState(null);
  const [loginTicket, setLoginTicket]     = useState(null);
  const [userEmail, setUserEmail]         = useState("");
  const [userName, setUserName]           = useState("");
  const [availableMethods, setAvailableMethods] = useState([]);
  const [twoFAMethod, setTwoFAMethod]     = useState(null);
  const [otpCode, setOtpCode]             = useState("");

  // Login form
  const [loginEmail, setLoginEmail]       = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [showLoginPwd, setShowLoginPwd]   = useState(false);

  // Signup sub-flow:
  // Sign-up sub-flow.
  //
  // Session 119 — DIY and Lawyer self-signup were removed. Every
  // new account created via the public signup form is now a Client
  // tied to a law-firm code. Lawyers / Admins are seeded by the
  // master lawyer in /accounts; DIY is no longer surfaced anywhere
  // public.
  //
  // The previous three-step role picker → confirm → form flow is
  // replaced by a single-step form. We still keep `signupSubStep`
  // as a piece of state (rather than ripping it out) so the form
  // and submit handler that gate on `signupSubStep === "form"`
  // still work without a wider refactor.
  const [signupSubStep, setSignupSubStep] = useState("form");
  const [signupType, setSignupType]       = useState("Client");  // "Client" | "DIY" (toggle on the form)
  const [reg, setReg] = useState({
    email: "", phone: "", password: "", confirmPassword: "", firmCode: "",
    first_name: "", last_name: "",
  });
  const [showRegPwd, setShowRegPwd]         = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);

  // Firm-code live validation (against GET /api/auth/validate-firm-code)
  //   null | "checking" | "valid" | "invalid"
  const [firmCodeStatus, setFirmCodeStatus]     = useState(null);
  const [firmCodeFirmName, setFirmCodeFirmName] = useState("");
  const firmCodeTimer = React.useRef(null);

  // Forgot password
  const [forgotEmail, setForgotEmail]   = useState("");
  const [forgotTicket, setForgotTicket] = useState(null);
  const [forgotOtp, setForgotOtp]       = useState("");
  const [newPassword, setNewPassword]   = useState("");
  const [showNewPwd, setShowNewPwd]     = useState(false);

  // CAPTCHA refs/tokens removed — reCAPTCHA v3 executes silently on submit

  const clear = () => { setError(""); setSuccessMsg(""); };

  // Reset tabs — going back to the signup tab resets the firm-code
  // validator state. Session 119: the sub-step + type are pinned to
  // ("form", "Client") permanently since the role picker is gone.
  const handleTabChange = (v) => {
    setActiveTab(v);
    if (v === "signup") {
      setSignupSubStep("form");
      setSignupType("Client");
      setFirmCodeStatus(null);
      setFirmCodeFirmName("");
    }
    clear();
  };

  // ── Firm code validation ────────────────────────────────────────────────
  const validateFirmCode = async (codeRaw) => {
    const code = (codeRaw || "").trim().toUpperCase();
    if (!code) { setFirmCodeStatus(null); setFirmCodeFirmName(""); return; }
    setFirmCodeStatus("checking");
    try {
      const res = await axios.get(`${API}/auth/validate-firm-code`, { params: { code } });
      if (res.data.valid) {
        setFirmCodeStatus("valid");
        setFirmCodeFirmName(res.data.firm_name || "");
      } else {
        setFirmCodeStatus("invalid");
        setFirmCodeFirmName("");
      }
    } catch (err) {
      // Session 255 — a throttled check (429) is NOT a bad code; don't tell
      // the user their firm code is wrong when we simply didn't look.
      if (err?.response?.status === 429) {
        setFirmCodeStatus(null);
        setFirmCodeFirmName("");
        setError(err.response.data?.detail || "Too many code checks. Please wait a minute.");
        return;
      }
      setFirmCodeStatus("invalid");
      setFirmCodeFirmName("");
    }
  };

  const handleFirmCodeChange = (raw) => {
    const code = raw.toUpperCase();
    setReg({ ...reg, firmCode: code });
    setFirmCodeStatus(null);
    setFirmCodeFirmName("");
    clearTimeout(firmCodeTimer.current);
    if (code.trim().length >= 2) {
      firmCodeTimer.current = setTimeout(() => validateFirmCode(code), 500);
    }
  };

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleLogin = async (e) => {
    e.preventDefault();
    clear();
    setIsLoading(true);
    try {
      const captchaToken = await getRecaptchaToken("login");
      const res = await axios.post(`${API}/auth/login`, {
        email: loginEmail, password: loginPassword, captcha_token: captchaToken,
      });
      setUserId(res.data.user_id);
      // Single-use proof that the password step passed — /auth/send-2fa and
      // /auth/verify-2fa reject a bare user_id (Session 273 security audit).
      setLoginTicket(res.data.login_ticket || null);
      setUserEmail(res.data.email);
      setUserName(res.data.first_name);
      setAvailableMethods(res.data.available_methods);
      setStep("2fa_method");
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed. Check your credentials.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    clear();
    if (!signupType) {
      setError("Please select an account type.");
      return;
    }
    if (reg.password !== reg.confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if ((signupType === "Client" || signupType === "Lawyer") && !reg.firmCode.trim()) {
      setError("A Law Firm Code is required for this account type.");
      return;
    }
    setIsLoading(true);
    try {
      const captchaToken = await getRecaptchaToken("register");
      const res = await axios.post(`${API}/auth/register`, {
        email: reg.email,
        first_name: reg.first_name,
        last_name: reg.last_name,
        password: reg.password,
        phone: reg.phone || undefined,
        user_type: signupType,
        firm_code: signupType === "DIY" ? undefined : (reg.firmCode.trim().toUpperCase() || undefined),
        captcha_token: captchaToken,
      });
      localStorage.setItem("compass_token", res.data.access_token);
      localStorage.setItem("compass_user_type", res.data.user_type);
      localStorage.setItem("compass_first_name", res.data.first_name || "");
      localStorage.setItem("compass_last_name", res.data.last_name || "");
      if (res.data.user_id) localStorage.setItem("compass_user_id", res.data.user_id);
      if (res.data.is_master) localStorage.setItem("compass_is_master", "true"); else localStorage.removeItem("compass_is_master");
      localStorage.removeItem("compass_onboarding_done");
      if (res.data.has_tenant || res.data.user_type === "DIY") {
        navigate("/onboarding");
      } else {
        navigate(landingPath(res.data.user_type));
      }
    } catch (err) {
      const detail = err.response?.data?.detail || "Registration failed.";
      setError(detail);
      // If the failure was an invalid firm code, mark the field as invalid
      // so the inline "create a DIY account instead" suggestion appears.
      if (/invalid law firm code/i.test(detail)) {
        setFirmCodeStatus("invalid");
        setFirmCodeFirmName("");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSend2FA = async (method) => {
    clear();
    setTwoFAMethod(method);
    setIsLoading(true);
    setOtpCode("");
    try {
      await axios.post(`${API}/auth/send-2fa`, { login_ticket: loginTicket, user_id: userId, method });
      setStep("2fa_verify");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to send verification code.");
      setTwoFAMethod(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify2FA = async () => {
    if (otpCode.length < 6) return;
    clear();
    setIsLoading(true);
    try {
      const res = await axios.post(`${API}/auth/verify-2fa`, {
        login_ticket: loginTicket, user_id: userId, method: twoFAMethod, code: otpCode,
      });
      localStorage.setItem("compass_token", res.data.access_token);
      localStorage.setItem("compass_user_type", res.data.user_type);
      localStorage.setItem("compass_first_name", res.data.first_name);
      localStorage.setItem("compass_last_name", res.data.last_name);
      if (res.data.user_id) localStorage.setItem("compass_user_id", res.data.user_id);
      if (res.data.is_master) localStorage.setItem("compass_is_master", "true"); else localStorage.removeItem("compass_is_master");
      localStorage.removeItem("compass_onboarding_done");
      if (res.data.user_type === "Client" || res.data.user_type === "DIY") {
        try {
          const obRes = await axios.get(`${API}/wizards/onboarding-status`, {
            headers: { Authorization: `Bearer ${res.data.access_token}` },
          });
          if (obRes.data.needs_onboarding) {
            navigate("/onboarding");
            return;
          }
        } catch (_) { /* ignore */ }
      }
      navigate(getNextPath() || landingPath(res.data.user_type));
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid verification code.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    clear();
    setIsLoading(true);
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setForgotTicket(res.data.reset_ticket);
      setStep("forgot_verify");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to send reset code.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // Google sign-in for the LOGIN tab always authenticates the existing
    // user — no user_type needed. (Sign-up via Google is disabled in the
    // unified flow because account type + firm code must be chosen first.)
    localStorage.removeItem("pending_google_user_type");
    localStorage.removeItem("pending_google_firm_code");
    const redirectUrl = window.location.origin + "/login";
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Session 222 — SIGNUP-tab Google button.
  // Wires a brand-new Google signup through the SAME onboarding
  // path as `/auth/register`: we validate the account-type +
  // firm-code selection UP-FRONT (before the OAuth redirect) and
  // stash them in localStorage. `AuthCallback.jsx` reads them back
  // when the user returns from Google, forwards them to
  // `POST /auth/google/session`, and the backend applies the
  // identical tenant/lawyers/cloud-folder logic. Result: a Google
  // signup lands on `/onboarding` exactly like an email/password
  // signup does — closing the pre-fix gap where Google-created
  // users had no tenant and skipped onboarding entirely.
  const handleGoogleSignup = () => {
    clear();
    if (!signupType) {
      setError("Please select an account type before continuing with Google.");
      return;
    }
    if ((signupType === "Client" || signupType === "Lawyer") && !reg.firmCode.trim()) {
      setError("A Law Firm Code is required to sign up with Google for this account type.");
      return;
    }
    if ((signupType === "Client" || signupType === "Lawyer") && firmCodeStatus !== "valid") {
      setError("Please enter a valid Law Firm Code before continuing with Google.");
      return;
    }
    localStorage.setItem("pending_google_user_type", signupType);
    if (signupType !== "DIY" && reg.firmCode.trim()) {
      localStorage.setItem("pending_google_firm_code", reg.firmCode.trim().toUpperCase());
    } else {
      localStorage.removeItem("pending_google_firm_code");
    }
    const redirectUrl = window.location.origin + "/login";
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    clear();
    setIsLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        reset_ticket: forgotTicket, code: forgotOtp, new_password: newPassword,
      });
      setSuccessMsg("Password reset! Please sign in.");
      setStep("credentials");
      setActiveTab("login");
      setLoginEmail(forgotEmail);
      setForgotEmail("");
      setForgotOtp("");
      setNewPassword("");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to reset password.");
    } finally {
      setIsLoading(false);
    }
  };

  // ── 2FA Method Select ──────────────────────────────────────────────────────
  if (step === "2fa_method") {
    return (
      <PageShell>
        <div className="rounded-xl border bg-white shadow-2xl">
          <div className="p-6 text-center">
            <div className="font-semibold text-gray-900 text-base">Two-Factor Authentication</div>
            <div className="text-sm text-gray-500 mt-1">
              Welcome back, {userName}! Choose how to receive your code.
            </div>
          </div>
          <div className="px-6 pb-6 space-y-3">
            <ErrorMsg msg={error} testId="2fa-method-error" />
            {availableMethods.includes("email") && (
              <button onClick={() => handleSend2FA("email")} disabled={isLoading}
                className="w-full flex items-center gap-4 p-4 border-2 rounded-xl hover:border-[#001C60] hover:bg-blue-50 transition-all duration-200 text-left group disabled:opacity-50"
                data-testid="2fa-email-button">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center group-hover:bg-blue-200 transition-colors flex-shrink-0">
                  <Mail className="w-5 h-5 text-[#001C60]" />
                </div>
                <div>
                  <div className="font-medium text-gray-900">Email</div>
                  <div className="text-sm text-gray-500">Send code to {userEmail}</div>
                </div>
              </button>
            )}
            {availableMethods.includes("sms") && (
              <button onClick={() => handleSend2FA("sms")} disabled={isLoading}
                className="w-full flex items-center gap-4 p-4 border-2 rounded-xl hover:border-[#001C60] hover:bg-blue-50 transition-all duration-200 text-left group disabled:opacity-50"
                data-testid="2fa-sms-button">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center group-hover:bg-blue-200 transition-colors flex-shrink-0">
                  <Phone className="w-5 h-5 text-[#001C60]" />
                </div>
                <div>
                  <div className="font-medium text-gray-900">SMS</div>
                  <div className="text-sm text-gray-500">Send code to your phone number</div>
                </div>
              </button>
            )}
            <button onClick={() => { setStep("credentials"); clear(); }}
              className="w-full text-sm text-gray-500 hover:text-[#001C60] flex items-center justify-center gap-2 mt-2 transition-colors"
              data-testid="back-to-login-button">
              <ArrowLeft className="w-4 h-4" /> Back to login
            </button>
          </div>
        </div>
      </PageShell>
    );
  }

  // ── OTP Verify ─────────────────────────────────────────────────────────────
  if (step === "2fa_verify") {
    return (
      <PageShell>
        <div className="rounded-xl border bg-white shadow-2xl">
          <div className="p-6 text-center">
            <div className="font-semibold text-gray-900 text-base">Enter Verification Code</div>
            <div className="text-sm text-gray-500 mt-1">
              {twoFAMethod === "email" ? `Code sent to ${userEmail}` : "Code sent to your phone number"}
            </div>
          </div>
          <div className="px-6 pb-6 space-y-4">
            <ErrorMsg msg={error} testId="2fa-verify-error" />
            <div className="flex justify-center py-2">
              <InputOTP maxLength={6} value={otpCode} onChange={setOtpCode} data-testid="otp-input">
                <InputOTPGroup>
                  {[0,1,2,3,4,5].map((i) => <InputOTPSlot key={i} index={i} />)}
                </InputOTPGroup>
              </InputOTP>
            </div>
            <NavyButton onClick={handleVerify2FA} disabled={isLoading || otpCode.length < 6} data-testid="verify-otp-button">
              {isLoading ? "Verifying..." : "Verify Code"}
            </NavyButton>
            <div className="flex items-center justify-between text-sm pt-1">
              <button onClick={() => { setStep("2fa_method"); setOtpCode(""); clear(); }}
                className="text-gray-500 hover:text-[#001C60] flex items-center gap-1 transition-colors"
                data-testid="change-method-button">
                <ArrowLeft className="w-4 h-4" /> Change method
              </button>
              <button onClick={() => handleSend2FA(twoFAMethod)} disabled={isLoading}
                className="hover:underline flex items-center gap-1 disabled:opacity-50 transition-colors"
                style={{ color: "#001C60" }} data-testid="resend-otp-button">
                <RotateCcw className="w-3 h-3" /> Resend code
              </button>
            </div>
          </div>
        </div>
      </PageShell>
    );
  }

  // ── Forgot Password – Send ─────────────────────────────────────────────────
  if (step === "forgot_pw") {
    return (
      <PageShell>
        <div className="rounded-xl border bg-white shadow-2xl">
          <div className="p-6 text-center pb-2">
            <div className="font-semibold text-gray-900">Reset Your Password</div>
            <div className="text-sm text-gray-500 mt-1">Enter your email to receive a reset code</div>
          </div>
          <div className="px-6 pb-6">
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <ErrorMsg msg={error} testId="forgot-error" />
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                  <input type="email" required value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm pl-10 shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    data-testid="forgot-email-input" />
                </div>
              </div>
              <NavyButton type="submit" disabled={isLoading} data-testid="forgot-submit-button">
                {isLoading ? "Sending..." : "Send Reset Code"}
              </NavyButton>
              <button type="button" onClick={() => { setStep("credentials"); clear(); }}
                className="w-full text-sm text-gray-500 hover:text-[#001C60] flex items-center justify-center gap-2 transition-colors">
                <ArrowLeft className="w-4 h-4" /> Back to login
              </button>
            </form>
          </div>
        </div>
      </PageShell>
    );
  }

  // ── Forgot Password – Verify & Reset ──────────────────────────────────────
  if (step === "forgot_verify") {
    return (
      <PageShell>
        <div className="rounded-xl border bg-white shadow-2xl">
          <div className="p-6 text-center pb-2">
            <div className="font-semibold text-gray-900">Set New Password</div>
            <div className="text-sm text-gray-500 mt-1">Enter the code sent to {forgotEmail}</div>
          </div>
          <div className="px-6 pb-6">
            <form onSubmit={handleResetPassword} className="space-y-4">
              <ErrorMsg msg={error} testId="reset-error" />
              <SuccessMsg msg={successMsg} testId="reset-success" />
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">6-Digit Reset Code</label>
                <div className="flex justify-center py-1">
                  <InputOTP maxLength={6} value={forgotOtp} onChange={setForgotOtp}>
                    <InputOTPGroup>
                      {[0,1,2,3,4,5].map((i) => <InputOTPSlot key={i} index={i} />)}
                    </InputOTPGroup>
                  </InputOTP>
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">New Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                  <input type={showNewPwd ? "text" : "password"} value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="••••••••" required
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm pl-10 pr-10 shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    data-testid="new-password-input" />
                  <button type="button" tabIndex={-1} onClick={() => setShowNewPwd(!showNewPwd)}
                    className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600">
                    {showNewPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <PasswordStrength password={newPassword} />
              </div>
              <NavyButton type="submit" disabled={isLoading || forgotOtp.length < 6} data-testid="reset-submit-button">
                {isLoading ? "Resetting..." : "Reset Password"}
              </NavyButton>
              <button type="button" onClick={() => { setStep("forgot_pw"); clear(); }}
                className="w-full text-sm text-gray-500 hover:text-[#001C60] flex items-center justify-center gap-2 transition-colors">
                <ArrowLeft className="w-4 h-4" /> Use a different email
              </button>
            </form>
          </div>
        </div>
      </PageShell>
    );
  }

  // ── Credentials (Login / Sign Up) ──────────────────────────────────────────

  // Session 116 — render a small placeholder while we validate any
  // existing token so we don't flash the login form to an
  // already-authenticated user during the auto-redirect to /dashboard.
  if (bootstrapping) {
    return (
      <PageShell>
        <div className="rounded-xl border bg-white text-card-foreground shadow-2xl p-12 flex items-center justify-center"
             data-testid="login-bootstrapping">
          <Loader2 size={20} className="animate-spin text-[#001C60]" />
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <Helmet>
        <title>Log In — Compass Client &amp; Firm Portal | King @ Law</title>
        <meta name="robots" content="noindex,follow" />
      </Helmet>
      <h1 className="sr-only">Log in to the Compass family law portal by King @ Law</h1>
      <div className="rounded-xl border bg-white text-card-foreground shadow-2xl" data-testid="login-card">
        {/* Card header — "Compass" + tagline */}
        <div className="p-6 text-center pb-2">
          <div className="font-bold text-gray-900 text-lg" style={{ color: "#001C60" }}>
            Compass
          </div>
          <div className="text-sm text-muted-foreground mt-0.5">
            Your North Carolina family law sidekick
          </div>
        </div>

        <div className="p-6 pt-0">
          <Tabs value={activeTab} onValueChange={handleTabChange}>
            <TabsList className="w-full grid grid-cols-2 mb-4">
              <TabsTrigger value="login" data-testid="login-tab">Login</TabsTrigger>
              <TabsTrigger value="signup" data-testid="signup-tab">Sign Up</TabsTrigger>
            </TabsList>

            {/* ── Login Form ── */}
            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <ErrorMsg msg={error} testId="login-error" />
                <SuccessMsg msg={successMsg} testId="login-success-msg" />
                <div className="space-y-2">
                  <label className="text-sm font-medium leading-none text-gray-700" htmlFor="login-email">
                    Email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" aria-hidden="true" />
                    <input id="login-email" type="email" required
                      value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm pl-10 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                      data-testid="login-email-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium leading-none text-gray-700" htmlFor="login-password">
                      Password
                    </label>
                    <button type="button"
                      onClick={() => { setForgotEmail(loginEmail); setStep("forgot_pw"); clear(); }}
                      className="text-xs hover:underline transition-colors"
                      style={{ color: "#001C60" }} data-testid="forgot-password-link">
                      Forgot password?
                    </button>
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" aria-hidden="true" />
                    <input id="login-password" type={showLoginPwd ? "text" : "password"} required
                      value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)}
                      placeholder="••••••••"
                      className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm pl-10 pr-10 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                      data-testid="login-password-input" />
                    <button type="button" tabIndex={-1} onClick={() => setShowLoginPwd(!showLoginPwd)}
                      className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600 focus:outline-none transition-colors"
                      aria-label={showLoginPwd ? "Hide password" : "Show password"}>
                      {showLoginPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                {/* reCAPTCHA v3 runs silently on submit — Google disclosure required */}
                <p className="text-center text-xs text-gray-500 leading-relaxed">
                  Protected by reCAPTCHA.{" "}
                  <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline">Privacy</a>
                  {" & "}
                  <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="underline">Terms</a>
                </p>

                <NavyButton type="submit" disabled={isLoading} data-testid="login-submit-button">
                  {isLoading ? "Signing in..." : "Sign In"}
                </NavyButton>
              </form>
              <GoogleLoginSection onClick={handleGoogleLogin} />
            </TabsContent>

            {/* ── Sign Up Flow ──
                Session 119: simplified to a Client-only single-step
                form. The previous role-picker + confirm screens
                were removed (DIY de-emphasised, Lawyers seeded by
                the master in /accounts). Components SignupRolePicker
                and SignupConfirm are left defined above but never
                rendered — the next refactor that touches this file
                can delete them. */}
            <TabsContent value="signup">
              {signupSubStep === "form" && signupType && (
                <>
                  <SignupTypeToggle
                    type={signupType}
                    onChange={(t) => { setSignupType(t); clear(); }}
                  />
                  <form onSubmit={handleRegister} className="space-y-4" data-testid={`signup-form-${signupType.toLowerCase()}`}>
                    <ErrorMsg msg={error} testId="signup-error" />
                    <SuccessMsg msg={successMsg} testId="signup-success-msg" />

                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">First Name</label>
                        <input type="text" required value={reg.first_name}
                          onChange={(e) => setReg({ ...reg, first_name: e.target.value })}
                          placeholder="Jane"
                          className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                          data-testid="signup-first-name-input" />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Last Name</label>
                        <input type="text" required value={reg.last_name}
                          onChange={(e) => setReg({ ...reg, last_name: e.target.value })}
                          placeholder="Doe"
                          className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                          data-testid="signup-last-name-input" />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Email</label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                        <input type="email" required value={reg.email}
                          onChange={(e) => setReg({ ...reg, email: e.target.value })}
                          placeholder="you@example.com"
                          className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm pl-10 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                          data-testid="signup-email-input" />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                        Phone <span className="text-gray-400 font-normal text-xs">(optional)</span>
                      </label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                        <input type="tel" value={reg.phone}
                          onChange={(e) => setReg({ ...reg, phone: e.target.value })}
                          placeholder="+1 (555) 000-0000"
                          className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm pl-10 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                          data-testid="signup-phone-input" />
                      </div>
                    </div>

                    {/* Firm Code — REQUIRED for Client and Lawyer */}
                    {(signupType === "Client" || signupType === "Lawyer") && (
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">
                          Law Firm Code <span className="text-red-500 ml-0.5">*</span>
                        </label>
                        <p className="text-xs text-gray-500">
                          {signupType === "Client"
                            ? "Enter the code provided by your law firm to link your account."
                            : "Enter your firm's code to join. New firms must be provisioned by the platform admin."}
                        </p>
                        <div className="relative">
                          <Hash className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                          <input type="text" required value={reg.firmCode}
                            onChange={(e) => handleFirmCodeChange(e.target.value)}
                            onBlur={() => {
                              // Session 255 — only fill a gap. Re-validating
                              // here would flip the status to "checking" on the
                              // blur a submit click causes, disabling the
                              // button before the click lands.
                              if (reg.firmCode.trim() && !firmCodeStatus) validateFirmCode(reg.firmCode);
                            }}
                            className={`flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm pl-10 pr-10 font-mono tracking-wider focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring ${
                              firmCodeStatus === "invalid" ? "border-red-400" :
                              firmCodeStatus === "valid"   ? "border-green-400" :
                              "border-input"
                            }`}
                            data-testid="signup-firm-code-input" />
                          {firmCodeStatus === "checking" && <Loader2 className="absolute right-3 top-2.5 h-4 w-4 text-gray-400 animate-spin" />}
                          {firmCodeStatus === "valid"    && <CheckCircle className="absolute right-3 top-2.5 h-4 w-4 text-green-500" />}
                          {firmCodeStatus === "invalid"  && <AlertCircle className="absolute right-3 top-2.5 h-4 w-4 text-red-500" />}
                        </div>

                        {firmCodeStatus === "valid" && firmCodeFirmName && (
                          <p className="text-xs text-green-700 flex items-center gap-1" data-testid="firm-code-valid-msg">
                            <CheckCircle size={12} /> Linking to <strong className="ml-0.5">{firmCodeFirmName}</strong>
                          </p>
                        )}

                        {firmCodeStatus === "invalid" && (
                          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-800"
                               data-testid="firm-code-invalid-msg">
                            <p>
                              <strong>{reg.firmCode}</strong> is not a valid firm code.{" "}
                              Did you intend to create a{" "}
                              <button type="button" onClick={() => { setSignupType("DIY"); clear(); }}
                                className="font-semibold underline hover:text-red-900"
                                data-testid="firm-code-switch-diy">DIY account</button>?
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Password</label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                        <input type={showRegPwd ? "text" : "password"} required
                          value={reg.password} onChange={(e) => setReg({ ...reg, password: e.target.value })}
                          placeholder="••••••••"
                          className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm pl-10 pr-10 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                          data-testid="signup-password-input" />
                        <button type="button" tabIndex={-1} onClick={() => setShowRegPwd(!showRegPwd)}
                          className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600">
                          {showRegPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                      <PasswordStrength password={reg.password} />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Confirm Password</label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                        <input type={showConfirmPwd ? "text" : "password"} required
                          value={reg.confirmPassword}
                          onChange={(e) => setReg({ ...reg, confirmPassword: e.target.value })}
                          placeholder="••••••••"
                          className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm pl-10 pr-10 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                          data-testid="signup-confirm-password-input" />
                        <button type="button" tabIndex={-1} onClick={() => setShowConfirmPwd(!showConfirmPwd)}
                          className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600">
                          {showConfirmPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                      {reg.confirmPassword && reg.password !== reg.confirmPassword && (
                        <p className="text-xs text-red-500">Passwords do not match</p>
                      )}
                    </div>

                    <p className="text-center text-xs text-gray-500 leading-relaxed">
                      Protected by reCAPTCHA.{" "}
                      <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline">Privacy</a>
                      {" & "}
                      <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="underline">Terms</a>
                    </p>

                    <NavyButton
                      type="submit"
                      disabled={isLoading ||
                        ((signupType === "Client" || signupType === "Lawyer") &&
                          (!reg.firmCode.trim() || firmCodeStatus === "invalid"))}
                      data-testid="signup-submit-button">
                      {isLoading ? "Creating account..." : "Create Account"}
                    </NavyButton>
                  </form>
                  {/* Session 222 — Google signup button. Same
                      onboarding path as email/password: backend
                      applies the user_type + firm_code we stashed
                      in localStorage. */}
                  <GoogleLoginSection
                    onClick={handleGoogleSignup}
                    testId="google-signup-button"
                  />
                </>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </PageShell>
  );
};

export default LoginPage;
